import React, { useEffect, useState } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleDown,
  faAngleUp,
  faArrowLeft,
  faCar,
  faCaretDown,
  faCaretRight,
  faMapMarkerAlt,
  faPlusCircle,
  faTimes,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';
import utils from '../utils/utils';
import { useRoutePlan, useRoutePlanEdit } from './apiHooks';
import Loader from '../components/loader';
import ErrorMessage from '../components/errorMessage';
import { useDeliveries, useDeliveryCreate, useDeliveryDelete, useRouteDelete, useRoutes } from './view/apiHooks';
import DeliveryForm from './view/deliveryForm';
import Modal from 'react-bootstrap/Modal';
import RoutePlanVehiclePreview from './plans/routePlanVehiclePreview';
import DATE_FORMAT from '../components/constants/dateFormat';
import DeliveryPreview from './view/deliveryPreview';
import RoutePlanForm from './plans/routePlanForm';
import RoutePreview from './view/routePreview';
import randomColor from 'randomcolor';
import RouteForm from './view/routeForm';
import { connect } from 'react-redux';
import { FlatfileButton } from '@flatfile/react';

const RoutePlannerViewSidebar = ({
  match,
  activeDelivery,
  setActiveDelivery,
  activeRouteWithColor,
  setActiveRouteWithColor,
  currentUser,
}) => {
  const { formatMessage } = useIntl();

  const [openXs, setOpenXs] = useState(false);
  const [showAllDeliveries, setShowAllDeliveries] = useState(false);
  const [showRoutePlanEdit, setShowRoutePlanEdit] = useState(null);
  const [showDeliveryCreate, setShowDeliveryCreate] = useState(false);
  const [routesWithColors, setRoutesWithColors] = useState([]);

  const { routePlan, isLoading, isError, error } = useRoutePlan({
    routePlanId: match?.params?.routePlanId,
  });

  const {
    deliveries,
    isLoading: deliveriesLoading,
    isDeliveryError,
    deliveryError,
  } = useDeliveries({
    routePlanId: routePlan?.id,
  });

  const { routes } = useRoutes({
    routePlanId: routePlan?.id,
  });

  useEffect(() => {
    setRoutesWithColors(routes?.map((route) => ({ route, color: randomColor() })) || []);
  }, [routes]);

  const { editRoutePlan, isLoading: editLoading, isError: isEditError, error: editError } = useRoutePlanEdit(() => {});

  const deliveryDate = routePlan ? utils.getDateFromString(routePlan.deliveryDate) : null;

  const onDeliveryClick = (delivery) => {
    setActiveDelivery(delivery);
  };

  const {
    deleteDelivery,
    isLoading: deleteDeliveryLoading,
    isError: isDeleteDeliveryError,
    error: deleteDeliveryError,
  } = useDeliveryDelete({ onSuccess: () => setActiveDelivery(null) });

  const {
    deleteRoute,
    isLoading: deleteRouteLoading,
    isError: isDeleteRouteError,
    error: deleteRouteError,
  } = useRouteDelete({ onSuccess: () => setActiveRouteWithColor(null) });

  const {
    createDelivery,
    isLoading: createDeliveryLoading,
    isError: isCreateDeliveryError,
    error: createDeliveryError,
  } = useDeliveryCreate((delivery) => {});

  const createDeliveries = ({ validData }) => {
    createDelivery({
      deliveries: validData.map(({ address, lat, lon, unit, ...other }) => ({
        routePlanId: routePlan?.id,
        address: {
          street: address,
          unit: unit,
          coordinates:
            lat && lon
              ? {
                  lat,
                  lon,
                }
              : null,
        },
        ...other,
      })),
    });
  };

  const isOptimised =
    routes?.length &&
    deliveries?.length &&
    deliveries.every((delivery) => routes?.some((route) => route.deliveries.some((d) => d.id === delivery.id)));

  const deliveriesWithoutRoutes =
    routes?.length && deliveries?.length
      ? deliveries.filter((delivery) => {
          return !routes.some((r) => r.deliveries.some((d) => d.id === delivery.id));
        })
      : [];

  return (
    <>
      <div className={`route-planner-sidebar ${openXs ? 'm-open-xs' : ''}`}>
        <div className="route-planner-sidebar__top m-b-3">
          <div className="m-b-2">
            <Link
              to={`/routePlanner${deliveryDate ? `/date/${deliveryDate.format(DATE_FORMAT.url)}` : ''}`}
              className={'link'}
            >
              <small>
                <FontAwesomeIcon icon={faArrowLeft} /> <FormattedMessage id={'route.planner.view.sidebar.back'} />
              </small>
            </Link>
          </div>
          <div className="route-planner-sidebar__top-toggle" onClick={() => setOpenXs(!openXs)}>
            {openXs ? <FontAwesomeIcon icon={faAngleUp} /> : <FontAwesomeIcon icon={faAngleDown} />}
          </div>
        </div>

        <Loader show={isLoading || deliveriesLoading || editLoading} />
        {isError || isDeliveryError || isEditError ? (
          <ErrorMessage error={utils.getErrorMessageFromResponse(error || deliveryError || editError)} />
        ) : (
          ''
        )}

        <h3 className="m-b-1">
          {routePlan?.name}{' '}
          <button className="btn btn-normal-link btn-small" onClick={() => setShowRoutePlanEdit(true)}>
            <FormattedMessage id={'route.planner.view.cta.edit'} />
          </button>
        </h3>

        <div className="m-b-2">
          <div>
            <small>🗓 {deliveryDate?.format(DATE_FORMAT.displayWeekdayAndDate)}</small>{' '}
          </div>
          <div>
            <small>
              🏠{' '}
              <FormattedMessage
                id={'route.planner.view.starting.address'}
                values={{ address: routePlan?.startingAddress?.street }}
              />
            </small>{' '}
          </div>
        </div>

        {!isOptimised ? (
          <div className="m-b-3">
            <button
              className={`btn btn-success btn-block btn-small ${editLoading ? 'btn-loading' : ''}`}
              disabled={editLoading}
              onClick={() => {
                editRoutePlan({ routePlan, optimise: true });
              }}
            >
              <FormattedMessage id={'route.planner.view.cta.optimize'} />
            </button>
          </div>
        ) : (
          ''
        )}

        {routesWithColors.length ? (
          <div className="m-b-6">
            <h4 className="m-b-3">
              <FormattedMessage id={'route.planner.view.routes.header'} />
            </h4>

            {routesWithColors?.map((routeWithColor) => {
              return (
                <RoutePreview
                  onClick={() => setActiveRouteWithColor(routeWithColor)}
                  key={routeWithColor.route.id}
                  route={routeWithColor.route}
                  color={routeWithColor.color}
                  active={activeRouteWithColor?.route?.id === routeWithColor.route.id}
                />
              );
            })}
          </div>
        ) : (
          <div className="m-b-6">
            <h4 className="m-b-3">
              <FormattedMessage id={'route.planner.view.vehicles.header'} />
            </h4>

            <div className="m-b-3">
              <button className="btn btn-normal-link" onClick={() => setShowRoutePlanEdit(routePlan)}>
                <FontAwesomeIcon icon={faCar} /> <FormattedMessage id={'route.planner.view.cta.edit.route.plan'} />
              </button>
            </div>

            {routePlan?.vehicles?.map((vehicle) => {
              return <RoutePlanVehiclePreview key={vehicle.id} vehicle={vehicle} />;
            })}
          </div>
        )}

        {deliveriesWithoutRoutes.length ? (
          <>
            <h4 className="m-b-3">
              <FormattedMessage
                id={'route.planner.view.deliveries.without.routes.header'}
                values={{ count: deliveriesWithoutRoutes.length }}
              />
            </h4>

            {deliveriesWithoutRoutes?.map((delivery) => {
              return (
                <DeliveryPreview
                  delivery={delivery}
                  key={delivery.id}
                  onClick={() => onDeliveryClick(delivery)}
                  isActive={activeDelivery?.id === delivery.id}
                />
              );
            })}
          </>
        ) : (
          ''
        )}

        <h4 className="m-b-3">
          <FormattedMessage id={'route.planner.view.deliveries.header'} values={{ count: deliveries?.length }} />
        </h4>

        <div className="m-b-3">
          {currentUser ? (
            <div className="m-b-1">
              {isCreateDeliveryError ? (
                <ErrorMessage error={utils.getErrorMessageFromResponse(createDeliveryError)} />
              ) : (
                ''
              )}

              <FlatfileButton
                className={`btn btn-default btn-block btn-small ${createDeliveryLoading ? 'btn-loading' : ''}`}
                licenseKey="9aa51fb3-827c-4ca2-80da-4abf1ab83d3b"
                customer={{
                  companyId: currentUser.activeBusinessProfile?.id,
                  companyName: currentUser.activeBusinessProfile?.companyName,
                  email: currentUser.contact,
                  name: currentUser.screenName,
                  userId: currentUser.id,
                }}
                settings={{
                  type: 'Contact',
                  fields: [
                    { label: 'Customer name', key: 'name', validators: [{ validate: 'required' }] },
                    { label: 'Address', key: 'address', validators: [{ validate: 'required' }] },
                    { label: 'Unit', key: 'unit', validators: [] },
                    {
                      label: 'Latitude (optional)',
                      description: 'If provided, will override the coordinates we get from the provided address.',
                      key: 'lat',
                      validators: [
                        {
                          validate: 'regex_matches',
                          regex: '[+-]?([0-9]*[.])?[0-9]+',
                          error:
                            'Your Latitude coordinate must be a valid number - make sure to use "." for the delimiter, e.g 59.421957.',
                        },
                      ],
                    },
                    {
                      label: 'Longitude (optional)',
                      description: 'If provided, will override the coordinates we get from the provided address.',
                      key: 'lon',
                      validators: [
                        {
                          validate: 'regex_matches',
                          regex: '[+-]?([0-9]*[.])?[0-9]+',
                          error:
                            'Your Longitude coordinate must be a valid number - make sure to use "." for the delimiter, e.g 59.421957.',
                        },
                      ],
                    },
                    { label: 'Phone number', key: 'phoneNumber', validators: [{ validate: 'required' }] },
                    { label: 'Order content', key: 'content', validators: [] },
                    {
                      label: 'Courier instructions (optional)',
                      key: 'instructions',
                      validators: [],
                      description:
                        'Optional additional instructions for the courier, e.g "Turn left at the yellow gate"',
                    },
                  ],
                  managed: true,
                }}
                onData={async ({ validData }) => {
                  createDeliveries({ validData });
                  return 'Done!';
                }}
                theme={{
                  buttons: {
                    primaryButton: {
                      backgroundColor: '#00c6ff',
                      color: '#002d3d',
                      border: '1px solid #00c6ff',
                      padding: '6px 16px',
                      fontSize: '15px',
                      borderRadius: '20px',
                      ':hover': {
                        backgroundColor: '#0D93BA',
                        border: '1px solid #0D93BA',
                      },
                    },
                  },
                }}
              >
                <FontAwesomeIcon icon={faMapMarkerAlt} />{' '}
                <FormattedMessage id={'route.planner.view.cta.import.stops'} />
              </FlatfileButton>
            </div>
          ) : (
            ''
          )}
          <div className="">
            <button className="btn btn-normal-link btn-small" onClick={() => setShowDeliveryCreate(true)}>
              <FontAwesomeIcon icon={faPlusCircle} /> <FormattedMessage id={'route.planner.view.cta.add.stops'} />
            </button>
          </div>
        </div>

        {routes?.length ? (
          <div className="m-b-3">
            <button className="btn btn-normal-link" onClick={() => setShowAllDeliveries(!showAllDeliveries)}>
              <FontAwesomeIcon icon={showAllDeliveries ? faCaretDown : faCaretRight} />{' '}
              {showAllDeliveries ? (
                <FormattedMessage id={'route.planner.view.deliveries.cta.hide'} />
              ) : (
                <FormattedMessage id={'route.planner.view.deliveries.cta.show'} />
              )}
            </button>
          </div>
        ) : (
          ''
        )}

        {!routes?.length || showAllDeliveries ? (
          <>
            {deliveries?.map((delivery) => {
              return (
                <DeliveryPreview
                  delivery={delivery}
                  key={delivery.id}
                  onClick={() => onDeliveryClick(delivery)}
                  isActive={activeDelivery?.id === delivery.id}
                />
              );
            })}
          </>
        ) : (
          ''
        )}
      </div>

      {activeDelivery ? (
        <div className="route-planner-sidebar-popup m-white-bg">
          <div onClick={() => setActiveDelivery(null)} className="route-planner-sidebar-popup__close">
            <FontAwesomeIcon icon={faTimes} />
          </div>

          <div className="m-b-3">
            {isDeleteDeliveryError ? (
              <ErrorMessage error={utils.getErrorMessageFromResponse(deleteDeliveryError)} />
            ) : (
              ''
            )}

            <button
              className={`btn btn-normal-link btn-small ${deleteDeliveryLoading ? 'btn-loading' : ''}`}
              onClick={() => {
                if (window.confirm(formatMessage({ id: 'route.planner.view.delivery.delete.confirm' }))) {
                  deleteDelivery({ delivery: activeDelivery });
                }
              }}
            >
              <FontAwesomeIcon icon={faTrashAlt} /> <FormattedMessage id={'route.planner.view.delivery.delete'} />
            </button>
          </div>
          <DeliveryForm
            compact={true}
            onSuccess={() => {
              setActiveDelivery(null);
            }}
            routePlanId={routePlan.id}
            deliveryId={activeDelivery.id}
          />
        </div>
      ) : (
        ''
      )}

      {activeRouteWithColor ? (
        <div className="route-planner-sidebar-popup m-white-bg">
          <div onClick={() => setActiveRouteWithColor(null)} className="route-planner-sidebar-popup__close">
            <FontAwesomeIcon icon={faTimes} />
          </div>

          <div className="m-b-3">
            {isDeleteRouteError ? <ErrorMessage error={utils.getErrorMessageFromResponse(deleteRouteError)} /> : ''}

            <div className="m-b-1">
              <button
                className={`btn btn-normal-link btn-small ${deleteRouteLoading ? 'btn-loading' : ''}`}
                onClick={async () => {
                  if (window.confirm(formatMessage({ id: 'route.planner.view.route.delete.confirm' }))) {
                    try {
                      deleteRoute({ route: activeRouteWithColor.route });
                    } catch (e) {
                      console.log({ e });
                    }
                  }
                }}
              >
                <FontAwesomeIcon icon={faTrashAlt} /> <FormattedMessage id={'route.planner.view.route.delete'} />
              </button>
            </div>
            <div>
              <small className="text-muted">
                <FormattedMessage id={'route.planner.view.route.delete.help'} />
              </small>
            </div>
          </div>
          <RouteForm
            deliveriesWithoutRoutes={deliveriesWithoutRoutes}
            compact={true}
            onSuccess={() => {
              setActiveRouteWithColor(null);
            }}
            routeId={activeRouteWithColor.route.id}
          />
        </div>
      ) : (
        ''
      )}

      <Modal show={!!showRoutePlanEdit} onHide={() => setShowRoutePlanEdit(false)} size="lg">
        <div onClick={() => setShowRoutePlanEdit(false)} className="b-modal-close">
          <FontAwesomeIcon icon={faTimes} />
        </div>

        <div className="b-modal">
          {showRoutePlanEdit && routePlan ? (
            <RoutePlanForm routePlanId={routePlan.id} onSuccess={() => setShowRoutePlanEdit(false)} />
          ) : (
            ''
          )}
        </div>
      </Modal>

      <Modal show={!!showDeliveryCreate} onHide={() => setShowDeliveryCreate(false)} size="lg">
        <div onClick={() => setShowDeliveryCreate(false)} className="b-modal-close">
          <FontAwesomeIcon icon={faTimes} />
        </div>

        <div className="b-modal">
          {showDeliveryCreate ? (
            <DeliveryForm onSuccess={() => setShowDeliveryCreate(false)} routePlanId={routePlan.id} />
          ) : (
            ''
          )}
        </div>
      </Modal>
    </>
  );
};

function mapStateToProps(state) {
  return {
    currentUser: state.currentUser.user,
  };
}

export default withRouter(connect(mapStateToProps)(RoutePlannerViewSidebar));
